import React, { useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import OfferAddToCartButton from '../../OfferBookButtons/OfferAddToCartButton'
import AlwaysOnMarginText from '../AlwaysOnMarginText'
import OfferSelectPackageButton from '../../OfferBookButtons/OfferSelectPackageButton'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import TextButton from 'components/Luxkit/Button/TextButton'
import AccommodationMobileViewOptionsButton from 'components/OfferPage/Accommodation/AccommodationMobileViewOptionsButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import ModalContext from 'contexts/ModalContext'
import AlwaysOnRoomDetailsModal from './Modals/AlwaysOnRoomDetailsModal'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

interface Props {
  packageView: OfferPackageOptionView;
  rooms: Array<App.Occupants>;
  offer: App.Offer;
  checkIn?: string;
  isFlightPrice?: boolean;
  canBuyNow?: boolean;
  valuedUpTo: {
    value: number;
    percent: number;
  };
  showFlightDurationLabel?: boolean;
  detailsModalMode: 'room-and-package' | 'room-only';
  isFlightsSelected?: boolean;
  flightOrigin?: App.Airport
  availableForPurchase?: boolean;
}

function AlwaysOnCheapestPriceDetailsMobile(props: Props) {
  const {
    rooms,
    offer,
    checkIn,
    isFlightPrice,
    canBuyNow,
    valuedUpTo,
    showFlightDurationLabel,
    detailsModalMode,
    isFlightsSelected,
    flightOrigin,
    packageView,
    availableForPurchase,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const userMultiItemCartEnabled = useAppSelector((state: App.State) => isMultiCartEnabled(state))
  const canAddToUserCart = userMultiItemCartEnabled && canBuyNow

  const pkg = packageView.package

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const hotelPrice = packageView.hotelPrice

  const canPurchase = availableForPurchase && packageView.capacityValid && packageView.hotelAvailable
  const isValidRoomOccupancy = packageView.flightsAvailable || offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && !(packageView.hotelAvailable && !packageView.capacityValid && availableForPurchase && !canPurchase) && isValidRoomOccupancy && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!packageView.flightPrice && !offer.flightCacheDisabled

  const showFlightPrice = isFlightPrice || (isFlightsSelected && flightsEnabled && hasFlights && !offer.bundledWithFlightsOnly)

  const propertyFees = packageView.propertyFees
  const totalPrice = (showFlightPrice ? hotelPrice + packageView.flightPrice : hotelPrice) + propertyFees
  const totalValuedUp = valuedUpTo.value + (showFlightPrice ? packageView.flightPrice : 0) + propertyFees
  const showPriceDetails = packageView.taxesAndFees > 0 || propertyFees || config.agentHub.isEnabled
  const shouldShowValue = valuedUpTo.value > packageView.hotelPrice && !pkg.roomRate?.inclusionsHideValue
  const businessCreditPrice = hotelPrice + propertyFees
  const isFromPrice = showFlightPrice || !checkIn

  const margin = pkg.roomRate?.marginPercentage
  const marginValue = margin ? hotelPrice * margin / 100 : 0
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(isSpoofed && showMarginInSpoofMode && margin && marginValue)

  const onShowViewDetails = useCallback(() => {
    if (detailsModalMode === 'room-and-package') {
      // Reset bedbank room id for Single Property pages
      offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: undefined })
      offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
    } else {
      showModal(<AlwaysOnRoomDetailsModal room={pkg.roomType!} />)
    }
  }, [detailsModalMode, offerPageDispatch, pkg.roomType, showModal])

  return <Group direction="vertical" gap={16}>
    <Group direction="vertical" gap={4}>
      <div>
        <PriceRowPriceCaption>
          {pkg.durationLabel.toLowerCase()} {(showFlightDurationLabel || showFlightPrice) && '+ flights'} {isFromPrice && 'from'}
        </PriceRowPriceCaption>
        <PriceRowPrice
          size="S"
          price={totalPrice}
          saleUnit="total"
        />
        <PriceRowTaxesAndFees />
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={totalValuedUp}
          data-testid="always-on-price-details-value"
        />}
      </div>
      {showIncludedGuestsExceeded && <AdditionalGuestsPopup
        complex={false}
        description="+ Extra guest surcharge"
        modalContent={pkg.roomPolicyDescription}
      />}
      {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
        type="exact"
        offer={offer}
        totalCost={businessCreditPrice}
        numberOfNights={pkg.duration}
        roomCount={rooms.length}
      />}
      {showPriceDetails && <OfferPriceDetailsRow
        trigger="price-row"
        triggerSize="M"
        offerPage
        offer={offer}
        duration={pkg.duration}
        propertyFees={packageView.propertyFees}
        hotelPrice={packageView.hotelPrice}
        taxesAndFees={packageView.taxesAndFees}
        flightPrice={showFlightPrice ? packageView.flightPrice : undefined}
        rooms={rooms}
        extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
        dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
        cta={<Group
          direction="vertical"
          tabletDirection="horizontal"
          horizontalAlign="stretch"
          tabletHorizontalAlign="end"
          gap={8}
        >
          {canAddToUserCart && !showFlightPrice && <OfferAddToCartButton
            size="large"
            type="submit"
            packageView={packageView}
          />}
          {canAddToUserCart && showFlightPrice && <OfferSelectPackageButton
            size="large"
            data-buytype="flights"
            packageView={packageView}
          />}
          {canBuyNow && (!userMultiItemCartEnabled || !showFlightPrice) && <OfferBookButton
            size="large"
            type="submit"
            data-buytype={showFlightPrice ? 'flights' : undefined}
            packageView={packageView}
          >
            Book now
          </OfferBookButton>}
        </Group>
        }
      />}
      {displayMargin && margin && <AlwaysOnMarginText
        margin={margin}
        marginValue={marginValue}
        type="ao_estimate"
      />}
    </Group>
    <Group direction="horizontal" horizontalAlign="stretch" gap={8}>
      <TextButton kind="secondary" size="large" onClick={onShowViewDetails}>View details</TextButton>
      <AccommodationMobileViewOptionsButton size="large" roomType={pkg.roomType} />
    </Group>
  </Group>
}

export default withOfferPageAndReduxState((state, appState) => ({
  flightOrigin: state.airport,
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
}))(AlwaysOnCheapestPriceDetailsMobile)
